// Hex_3Error.jsx — 0이면 NORMAL, 1이면 FAULT (비트맵3도 사용 비트는 1이면 FAULT)
// UI: 아이콘은 표에 정의된 비트 중 하나라도 FAULT면 🔴

import "./OcipError.css";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Modal, Button, Table } from "react-bootstrap";
import { getIvFieldValue } from "../../utils/ivErrorDataUtils";

/** 비트맵 정의
 *  - 공통 규칙: 0 = NORMAL, 1 = FAULT
 *  - Bitmap 3(Inverter State)도 사용 비트(Bit0, Bit2, Bit7)는 1이면 FAULT로 표기
 */
const AlarmModalData = {
  3: { // FAULT_1 ← Error[0] (Data0~Data3) : Solar Fault
    title: "FAULT_1 (Solar Fault)",
    rows: [
      { bit: "Bit3",  desc: "Solar Cell OV limit fault (태양전지 과전압 제한초과)", code: "0x08"  },
      { bit: "Bit4",  desc: "Solar Cell UV limit fault (태양전지 저전압 제한초과)", code: "0x10"  },
      { bit: "Bit11", desc: "Utility line failure (계통 전압 이상/정전)",         code: "0x800" },
    ],
  },
  4: { // FAULT_2 ← Error[1] (Data4~Data7) : Inverter Fault
    title: "FAULT_2 (Inverter Fault)",
    rows: [
      { bit: "Bit11", desc: "Inverter over current fault (인버터 과전류)", code: "0x800" },
    ],
  },
  5: { // FAULT_3 ← Error[2] (Data8~Data11) : Inverter State
    title: "FAULT_3 (Inverter State)",
    rows: [
      // Bit0: 0=On(정상), 1=Off(비정상) → 1이면 FAULT로 취급
      { bit: "Bit0", desc: "Contactor Off (MC off)", code: "0x01" },
      // Bit2: 0=Normal, 1=Fault
      { bit: "Bit2", desc: "Fuse fault (퓨즈 단선)",  code: "0x04" },
      // Bit7: 0=Normal, 1=Fault
      { bit: "Bit7", desc: "Over Temp (과온)",       code: "0x80" },
    ],
  },
  6: { // FAULT_4 ← Error[3] (Data12~Data15) : Operation Fault
    title: "FAULT_4 (Operation Fault)",
    rows: [
      { bit: "Bit0",  desc: "Inverter MC fault (인버터 MC 이상)",                 code: "0x01"  },
      { bit: "Bit2",  desc: "Inverter output voltage fault (출력 전압 이상)",        code: "0x04"  },
      { bit: "Bit3",  desc: "Inverter frequency fault (인버터 주파수 이상)",         code: "0x08"  },
      { bit: "Bit4",  desc: "Inverter–Line async fault (계통-인버터 위상 이상)",      code: "0x10"  },
      { bit: "Bit5",  desc: "Line phase sequence fault (계통 R,S,T상 역상)",         code: "0x20"  },
      { bit: "Bit6",  desc: "Line under voltage fault (계통 저전압 이상)",           code: "0x40"  },
      { bit: "Bit7",  desc: "Line over voltage fault (계통 과전압 이상)",            code: "0x80"  },
      { bit: "Bit8",  desc: "Line under frequency fault (계통 저주파수 이상)",        code: "0x100" },
      { bit: "Bit9",  desc: "Inverter O.C. overtime fault (인버터 과전류 시간초과)",   code: "0x200" },
      { bit: "Bit10", desc: "Line over frequency fault (계통 과주파수 이상)",         code: "0x400" },
    ],
  },
};

const Hex_3Error = ({ SelInverter }) => {
  const ivData  = useSelector((state) => state.ivData  || {});
  const [openModalNum, setOpenModalNum] = useState(null);
  const closeModal = () => setOpenModalNum(null);

  const getDikValue = (num) => {
    switch (num) {
      case 3: return Number(getIvFieldValue(ivData, "fault0", SelInverter, 0)) || 0; // Error[0]
      case 4: return Number(getIvFieldValue(ivData, "fault1", SelInverter, 0)) || 0; // Error[1]
      case 5: return Number(getIvFieldValue(ivData, "fault2", SelInverter, 0)) || 0; // Error[2]
      case 6: return Number(getIvFieldValue(ivData, "fault3", SelInverter, 0)) || 0; // Error[3]
      default: return 0;
    }
  };

  const parseBitFlags = (value) => {
    const n = Number(value) || 0;
    const flags = {};
    for (let i = 0; i <= 15; i++) flags[`Bit${i}`] = (n >> i) & 1; // 0 or 1
    return flags;
  };

  // 공통 규칙: 0 = NORMAL, 1 = FAULT
  const isBitFault = (bitVal) => (Number(bitVal) || 0) === 1;

  // 그룹 아이콘: 표에 정의된 비트 중 하나라도 FAULT면 🔴
  const isAbnormalGroup = (groupNum, value) => {
    const modal = AlarmModalData[groupNum];
    if (!modal) return false;
    const bits = parseBitFlags(value);
    return modal.rows.some((row) => isBitFault(bits[row.bit] ?? 0));
  };

  const renderModalContent = () => {
    const modal = AlarmModalData[openModalNum];
    if (!modal) return null;

    const rawValue = getDikValue(openModalNum);
    const bits = parseBitFlags(rawValue);

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title>
            {modal.title} (0x{rawValue.toString(16).toUpperCase()})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered size="sm">
            <thead>
              <tr>
                <th>Bit</th>
                <th>Description</th>
                <th>Mask</th>
                <th>Value</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {modal.rows.map((row, idx) => {
                const bitVal = bits[row.bit] ?? 0; // 0 or 1
                const fault = isBitFault(bitVal);   // 0=NORMAL, 1=FAULT
                return (
                  <tr key={idx} className={fault ? "active-row" : ""}>
                    <td>{row.bit}</td>
                    <td>{row.desc}</td>
                    <td>{row.code}</td>
                    <td>{bitVal}</td>
                    <td>{fault ? "FAULT" : "NORMAL"}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>닫기</Button>
        </Modal.Footer>
      </>
    );
  };

  const items = [
    { label: "FAULT_1 (Solar Fault)",    num: 3 },
    { label: "FAULT_2 (Inverter Fault)", num: 4 },
    { label: "FAULT_3 (State)",          num: 5 },
    { label: "FAULT_4 (Operation Fault)",num: 6 },
  ];

  return (
    <div className="status-list-container">
      <div className="status-list">
        {items.map(({ label, num }) => {
          const value = getDikValue(num);
          const abnormal = isAbnormalGroup(num, value);
          return (
            <div key={num} className="status-item">
              <span className="status-value">
                {label}: {value}
              </span>
              <button onClick={() => setOpenModalNum(num)}>
                <span className={abnormal ? "blinking-icon" : ""}>
                  {abnormal ? "🔴" : "🟢"}
                </span>
              </button>
            </div>
          );
        })}
      </div>

      {openModalNum !== null && (
        <Modal show onHide={closeModal} size="lg">
          {renderModalContent()}
        </Modal>
      )}
    </div>
  );
};

export default Hex_3Error;
